/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createHorizontalRuleNode, $isHorizontalRuleNode, HorizontalRuleNode, INSERT_HORIZONTAL_RULE_COMMAND } from "components/NewEditor/nodes/HorizontalRuleNode";
import { $insertNodeToNearestRoot, mergeRegister } from "@lexical/utils";
import { $getSelection, $isNodeSelection, $isRangeSelection, COMMAND_PRIORITY_EDITOR, createCommand, LexicalCommand } from "lexical";
import { useEffect } from "react";

export const FOCUS_HORIZONTAL_RULE_DUMMY_TEXTAREA_COMMAND: LexicalCommand<void> = createCommand("FOCUS_HORIZONTAL_RULE_DUMMY_TEXTAREA_COMMAND");

export function HorizontalRulePlugin(): null {
  const [editor] = useLexicalComposerContext();

  function $getHorizontalRuleNodeInSelection(): HorizontalRuleNode | null {
    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
      return null;
    }
    const nodes = selection.getNodes();
    const node = nodes[0];
    return $isHorizontalRuleNode(node) ? node : null;
  }

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        INSERT_HORIZONTAL_RULE_COMMAND,
        (type) => {
          const selection = $getSelection();

          if (!$isRangeSelection(selection)) {
            return false;
          }

          const focusNode = selection.focus.getNode();

          if (focusNode !== null) {
            const horizontalRuleNode = $createHorizontalRuleNode();
            $insertNodeToNearestRoot(horizontalRuleNode);
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        FOCUS_HORIZONTAL_RULE_DUMMY_TEXTAREA_COMMAND,
        () => {
          const horizontalRuleNode = $getHorizontalRuleNodeInSelection();
          if (horizontalRuleNode) {
            const horizontalRuleNodeKey = horizontalRuleNode.getKey();
            const horizontalRuleNodeElement = editor.getElementByKey(horizontalRuleNodeKey);
            if (horizontalRuleNodeElement) {
              const input = horizontalRuleNodeElement.querySelector("textarea");
              if (input) {
                input.focus();
              }
            }
          }
          return true;
        },
        COMMAND_PRIORITY_EDITOR
      )
    );
  }, [editor]);
  return null;
}
